import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import './DetailedArticle.scss';
import $ from 'jquery';
import { fromEnglish } from '../../translation';
import RatingGroup from '../RatingGroup/RatingGroup';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { ReactComponent as ChevronLeft } from '../../images/icons/chevron-left.svg';
import { Link } from 'react-router-dom';
import { isEmpty, uniq } from 'lodash';
import { IArticle, SearchService } from '../../services/search.service';
import { useService } from 'react-service-locator';

const DetailedArticle: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const searchService = useService(SearchService);

    const [ln, setLn] = useState(i18next.resolvedLanguage);
    const [loading, setLoading] = useState(true);

    const [doc, setDoc] = useState<IArticle | null>(null);

    const [conclusion, setConclusion] = useState<string>(t('components.detailed-article.fetching') as string);
    const [title, setTitle] = useState<string>('');
    const [keywords, setKeywords] = useState<string[]>([]);
    const [researchPopulation, setResearchPopulation] = useState<string | undefined>(undefined);

    useEffect(() => {
        const handler = (language: string) => setLn(language);
        i18next.on('languageChanged', handler);
        return () => i18next.off('languageChanged', handler);
    }, []);

    // Load doc: try current page first, otherwise fetch from API
    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);

            // 1) from current page if present
            const local = searchService.state.result?.items.find((c) => c.pmid === id);
            console.log(searchService.state);
            if (local) {
                if (!cancelled) {
                    setDoc({
                        ...local,
                        dois: [...local.dois],
                        keywords: [...local.keywords],
                        indicators: [...local.indicators],
                    });
                    setLoading(false);
                }
                return;
            }

            // 2) fetch by id (you need this endpoint)
            try {
                const accessToken = localStorage.getItem('accessToken');
                const date = new Date().toUTCString();
                const res = await fetch(`${process.env.REACT_APP_BASE_API_URI}/Document/get/${id}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'x-ms-date': date,
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                if (cancelled) return;

                if (!res.ok) {
                    setDoc(null);
                    setLoading(false);
                    return;
                }

                const fetched = (await res.json()) as IArticle;
                setDoc(fetched);
                setLoading(false);
            } catch {
                if (!cancelled) {
                    setDoc(null);
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [id, searchService.state.result?.items]); // ok to depend on items just for local lookup

    const getConclusion = async (pmid: string) => {
        const response = await fetch(
            `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/efetch.fcgi?db=pubmed&id=${pmid}&rettype=xml`,
        );
        const xml = await response.text();
        const data = $.parseXML(xml);

        let result = '';
        const searchTerms = ['Results', 'Result', 'Conclusions', 'Conclusion'];

        for (const searchTerm of searchTerms) {
            const lower = searchTerm.toLowerCase();
            const upper = searchTerm.toUpperCase();
            const cased = [upper, lower, searchTerm];

            for (const term of cased) {
                result = data.evaluate(
                    `/PubmedArticleSet/PubmedArticle/MedlineCitation/Article/Abstract/AbstractText[@Label="${term}"]`,
                    data,
                    null,
                    XPathResult.STRING_TYPE,
                ).stringValue;
                if (result) break;
            }
            if (result) break;

            for (const term of cased) {
                result = data.evaluate(
                    `/PubmedArticleSet/PubmedArticle/MedlineCitation/Article/Abstract/AbstractText[@NlmCategory="${term}"]`,
                    data,
                    null,
                    XPathResult.STRING_TYPE,
                ).stringValue;
                if (result) break;
            }
            if (result) break;
        }

        if (!result) {
            result = 'Conclusions of this study are not available, please see the abstract.';
        }

        return fromEnglish(result);
    };

    // Translate content when language or doc changes
    useEffect(() => {
        if (!doc) return;

        let cancelled = false;

        (async () => {
            setConclusion(t('components.detailed-article.fetching') as string);

            const translatedTitle = await fromEnglish(doc.title);

            const translatedKeywords = await Promise.all(
                (doc.keywords ?? [])
                    .map((c) => c.trim())
                    .filter((d) => !isEmpty(d))
                    .map((e) => fromEnglish(e ?? '')),
            );

            const translatedConclusion = await getConclusion(doc.pmid);

            let translatedPopulation: string | undefined = undefined;
            if (doc.researchPopulation) {
                translatedPopulation = await fromEnglish(doc.researchPopulation);
            }

            if (!cancelled) {
                setTitle(translatedTitle);
                setKeywords(uniq(translatedKeywords.map((e) => e.toLowerCase())));
                setConclusion(translatedConclusion);
                setResearchPopulation(translatedPopulation);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [ln, doc?.pmid]); // rerun when language changes or navigating to a new doc

    if (loading) {
        return <div className="DetailArticle card">{t('components.detailed-article.fetching')}</div>;
    }

    if (!doc) {
        return (
            <div className="DetailArticle card">
                <Link className="back-button" to="/results">
                    <ChevronLeft height={32} />
                </Link>
                <p className="body body-big">{t('pages.result-page.no-results-text')}</p>
            </div>
        );
    }

    return (
        <div className="DetailArticle card">
            <Link className="back-button" to="/results">
                <ChevronLeft height={32} />
            </Link>

            <div>
                <div className="keywords">
                    {uniq([searchService.state.lifestyleInterventionGroup, ...keywords])
                        .filter(Boolean)
                        .slice(0, 9)
                        .map((c, i) => (
                            <span key={'keyword-' + i} className="body body-normal">
                                {c}
                            </span>
                        ))}
                </div>

                <p className="body body-big research-population">
                    {doc.publicationDate && !isNaN(new Date(doc.publicationDate as any).getDate())
                        ? new Date(doc.publicationDate as any).toLocaleDateString()
                        : t('unknown-publication-date')}
                    {researchPopulation ? ` | ${t('study-population')}: ${researchPopulation}` : ''}
                </p>

                <h2>{title}</h2>
                <p className="body body-big conclusion">{conclusion}</p>
            </div>

            <RatingGroup article={doc}>
                <div className="relevant-links">
                    <h3>{t('components.detailed-article.relevant-links')}:</h3>

                    <a
                        className="body body-bold-big btn"
                        target="_blank"
                        href={`https://pubmed.ncbi.nlm.nih.gov/${doc.pmid}`}
                        rel="noreferrer"
                    >
                        <span className="material-icons-outlined">description</span>{' '}
                        {t('components.detailed-article.abstract')}
                    </a>

                    {!!doc.dois?.[0] && (
                        <a
                            href={`https://www.doi.org/${doc.dois[0]}`}
                            target="_blank"
                            rel="noreferrer"
                            className="body body-bold-big btn"
                        >
                            <span className="material-icons-outlined">link</span>{' '}
                            {t('components.detailed-article.full-article')}
                        </a>
                    )}
                </div>
            </RatingGroup>
        </div>
    );
};

export default DetailedArticle;
